import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ScrollText } from 'lucide-react';
import { PageBody, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TablePagination } from '@/components/TablePagination';
import { usePagination } from '@/hooks/use-pagination';
import { TableSearch } from '@/components/TableSearch';
import { useTableSearch } from '@/hooks/use-table-search';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queries';
import { formatDateTime } from '@/lib/utils';

export const Route = createFileRoute('/audit/')({ component: AuditLog });

const LIMIT = 100;

function AuditLog() {
  const logs = useQuery({
    queryKey: queryKeys.auditLogs(LIMIT),
    queryFn: () => api.auditLogs(LIMIT),
  });

  const search = useTableSearch(logs.data, (l) => [l.actor, l.entityType, l.entityId, l.action]);
  const pagination = usePagination(search.filtered);
  return (
    <>
      <PageHeader title="Audit log" description="Every console mutation and every identity merge" />
      <PageBody>
        <TableSearch
          search={search}
          placeholder="Search actor, entity or action"
          label="Search audit log"
          noun="entry"
        />
        <Card className="shadow-none">
          <CardContent className="p-0">
            {logs.isError ? (
              <ErrorState error={logs.error} onRetry={() => void logs.refetch()} />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Actor</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Changes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.isPending && <LoadingRows columns={5} />}
                  {pagination.pageRows.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="whitespace-nowrap">{formatDateTime(log.ts)}</TableCell>
                      <TableCell className="text-muted-foreground">{log.actor}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{log.entityType}</Badge>
                        <span className="ml-2 font-mono text-xs text-muted-foreground">
                          {log.entityId}
                        </span>
                      </TableCell>
                      <TableCell className="font-medium">{log.action}</TableCell>
                      <TableCell className="max-w-md truncate font-mono text-xs text-muted-foreground">
                        {Object.keys(log.changes).length > 0 ? JSON.stringify(log.changes) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <TablePagination pagination={pagination} label="entries" cappedAt={LIMIT} />
            {logs.data?.length === 0 && (
              <EmptyState
                title="Nothing logged yet"
                description="Campaign, creative, placement and identity changes all land here."
                icon={ScrollText}
              />
            )}
          </CardContent>
        </Card>
      </PageBody>
    </>
  );
}
